import React, { useState, useEffect, useContext, useRef } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftInput from 'components/SoftInput';
import SoftButton from 'components/SoftButton';
import { AuthContext } from 'context';
import { partnerService } from 'services/partnerService';
import { productService } from 'services/productService';
import { toPublicAssetUrl } from 'services/assets';

const CATEGORY_LABELS = {
  saas: 'SaaS-Produkte',
  iaas: 'IaaS-Produkte',
  ecm: 'UDS ECM',
  epm: 'UDS EP-Manager',
  auto: 'UDS Automation Pro',
  sboffice: 'SBOffice',
};

const withUploadCacheBuster = (url) => {
  if (!url) return url;
  return `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;
};

function PartnerBrandingSettings() {
  const { partnerSettings, setPartnerSettings } = useContext(AuthContext);
  const [brandName, setBrandName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#8b0000');
  const [logoUrl, setLogoUrl] = useState(null);
  const [faviconUrl, setFaviconUrl] = useState(null);
  const [portalDomain, setPortalDomain] = useState('');
  const [restrictedPortalEnabled, setRestrictedPortalEnabled] = useState(false);
  const [supportEmail, setSupportEmail] = useState('');
  const [supportPhone, setSupportPhone] = useState('');
  const [portalHomeTitle, setPortalHomeTitle] = useState('');
  const [portalHomeIntro, setPortalHomeIntro] = useState('');
  const [portalHomeSectionsText, setPortalHomeSectionsText] = useState('[]');
  const [allProducts, setAllProducts] = useState([]);
  const [selectedSlugs, setSelectedSlugs] = useState(null);
  const [customPrices, setCustomPrices] = useState({});
  const [customNames, setCustomNames] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingFavicon, setIsUploadingFavicon] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const logoInputRef = useRef(null);
  const faviconInputRef = useRef(null);

  useEffect(() => {
    if (partnerSettings) {
      setBrandName(partnerSettings.brand_name || '');
      setPrimaryColor(partnerSettings.primary_color || '#8b0000');
      setLogoUrl(partnerSettings.logo_url || null);
      setFaviconUrl(partnerSettings.favicon_url || null);
      setSelectedSlugs(partnerSettings.enabled_products || null);
      setCustomPrices(partnerSettings.custom_prices || {});
      setCustomNames(partnerSettings.custom_names || {});
      setPortalDomain(partnerSettings.portal_domain || '');
      setRestrictedPortalEnabled(partnerSettings.portal_mode === 'sboffice_iaas_restricted');
      setSupportEmail(partnerSettings.support_email || '');
      setSupportPhone(partnerSettings.support_phone || '');
      setPortalHomeTitle(partnerSettings.portal_home_title || '');
      setPortalHomeIntro(partnerSettings.portal_home_intro || '');
      setPortalHomeSectionsText(JSON.stringify(partnerSettings.portal_home_sections || [], null, 2));
    }
    productService.getProducts().then(setAllProducts).catch(() => setAllProducts([]));
  }, [partnerSettings]);

  const handleSaveSettings = async () => {
    setIsSaving(true);
    setSaveMessage('');
    try {
      var pricesToSave = {};
      Object.keys(customPrices).forEach(function(slug) {
        var val = customPrices[slug];
        if (val && val.trim() !== '') pricesToSave[slug] = val.trim();
      });

      var namesToSave = {};
      Object.keys(customNames).forEach(function(slug) {
        var val = customNames[slug];
        if (val && val.trim() !== '') namesToSave[slug] = val.trim();
      });

      let parsedHomeSections = null;
      if (portalHomeSectionsText.trim() !== '') {
        try {
          parsedHomeSections = JSON.parse(portalHomeSectionsText);
          if (!Array.isArray(parsedHomeSections)) {
            throw new Error('Portal-Abschnitte muessen eine JSON-Liste sein.');
          }
        } catch (parseError) {
          setSaveMessage('Fehler: Portal-Abschnitte enthalten kein gueltiges JSON-Array.');
          setIsSaving(false);
          return;
        }
      }

      const updated = await partnerService.saveMySettings({
        brand_name: brandName,
        primary_color: primaryColor,
        enabled_products: selectedSlugs,
        custom_prices: Object.keys(pricesToSave).length > 0 ? pricesToSave : null,
        custom_names: Object.keys(namesToSave).length > 0 ? namesToSave : null,
        portal_domain: portalDomain,
        portal_mode: restrictedPortalEnabled ? 'sboffice_iaas_restricted' : null,
        support_email: supportEmail,
        support_phone: supportPhone,
        portal_home_title: portalHomeTitle,
        portal_home_intro: portalHomeIntro,
        portal_home_sections: parsedHomeSections,
      });
      setPartnerSettings(updated);
      setSaveMessage('Einstellungen erfolgreich gespeichert.');
    } catch (err) {
      setSaveMessage(err?.response?.data?.error || 'Fehler beim Speichern der Einstellungen.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setIsUploadingLogo(true);
    setSaveMessage('');
    try {
      const result = await partnerService.uploadLogo(file);
      const cacheBustedLogoUrl = withUploadCacheBuster(result.logo_url);
      setLogoUrl(cacheBustedLogoUrl);
      setPartnerSettings(function(prev) { return Object.assign({}, prev, { logo_url: cacheBustedLogoUrl }); });
      setSaveMessage('Logo erfolgreich hochgeladen.');
    } catch (err) {
      setSaveMessage(err?.response?.data?.error || 'Fehler beim Hochladen des Logos.');
    } finally {
      setIsUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setIsUploadingFavicon(true);
    setSaveMessage('');
    try {
      const result = await partnerService.uploadFavicon(file);
      const cacheBustedFaviconUrl = withUploadCacheBuster(result.favicon_url);
      setFaviconUrl(cacheBustedFaviconUrl);
      setPartnerSettings(function(prev) { return Object.assign({}, prev, { favicon_url: cacheBustedFaviconUrl }); });
      setSaveMessage('Favicon erfolgreich hochgeladen.');
    } catch (err) {
      setSaveMessage(err?.response?.data?.error || 'Fehler beim Hochladen des Favicons.');
    } finally {
      setIsUploadingFavicon(false);
      e.target.value = '';
    }
  };

  const handleProductToggle = (slug) => {
    setSelectedSlugs(function(prev) {
      var current = prev !== null ? prev : allProducts.map(function(p) { return p.slug; });
      if (current.indexOf(slug) >= 0) return current.filter(function(s) { return s !== slug; });
      return current.concat([slug]);
    });
  };

  const handlePriceChange = (slug, value) => {
    setCustomPrices(function(prev) { return Object.assign({}, prev, { [slug]: value }); });
  };

  const handleNameChange = (slug, value) => {
    setCustomNames(function(prev) { return Object.assign({}, prev, { [slug]: value }); });
  };

  const isSlugSelected = (slug) => {
    if (selectedSlugs === null) return true;
    return selectedSlugs.indexOf(slug) >= 0;
  };

  var productsByCategory = {};
  allProducts.forEach(function(p) {
    if (!productsByCategory[p.category]) productsByCategory[p.category] = [];
    productsByCategory[p.category].push(p);
  });

  const logoPreviewUrl = toPublicAssetUrl(logoUrl);
  const faviconPreviewUrl = toPublicAssetUrl(faviconUrl);

  return (
    <SoftBox>

      {/* Markenname & Primärfarbe */}
      <SoftBox mb={3}>
        <Card>
          <SoftBox p={3}>
            <SoftTypography variant="h5" fontWeight="medium" gutterBottom>
              Markenname &amp; Primärfarbe
            </SoftTypography>
            <SoftTypography variant="body2" color="text" mb={2}>
              Der Markenname erscheint als Portal-Titel. Die Primärfarbe wird als Akzentfarbe verwendet (Hex-Code, z.B. #8b0000).
            </SoftTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SoftTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                  Markenname
                </SoftTypography>
                <SoftInput
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="z.B. Mein IT-Partner Portal"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                  Primärfarbe (Hex)
                </SoftTypography>
                <SoftBox display="flex" alignItems="center">
                  <SoftBox mr={1} style={{ flex: 1 }}>
                    <SoftInput
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      placeholder="#8b0000"
                      fullWidth
                    />
                  </SoftBox>
                  <SoftBox
                    width="40px"
                    height="40px"
                    borderRadius="8px"
                    border="1px solid #e2e8f0"
                    style={{ backgroundColor: primaryColor, flexShrink: 0 }}
                  />
                </SoftBox>
              </Grid>
            </Grid>
          </SoftBox>
        </Card>
      </SoftBox>

      {/* Logo */}
      <SoftBox mb={3}>
        <Card>
          <SoftBox p={3}>
            <SoftTypography variant="h5" fontWeight="medium" gutterBottom>
              Logo
            </SoftTypography>
            <SoftTypography variant="body2" color="text" mb={2}>
              Laden Sie Ihr Firmenlogo hoch (PNG, JPG, SVG – max. 2 MB). Es wird in der Seitenleiste angezeigt.
            </SoftTypography>
            {logoPreviewUrl && (
              <SoftBox mb={2}>
                <img
                  src={logoPreviewUrl}
                  alt="Logo Vorschau"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                  style={{ maxHeight: 80, maxWidth: 200, objectFit: 'contain', border: '1px solid #e2e8f0', borderRadius: 8, padding: 4 }}
                />
              </SoftBox>
            )}
            <input type="file" accept="image/*" ref={logoInputRef} style={{ display: 'none' }} onChange={handleLogoUpload} />
            <SoftButton
              variant="outlined" color="info"
              onClick={() => logoInputRef.current && logoInputRef.current.click()}
              disabled={isUploadingLogo}
            >
              {isUploadingLogo ? 'Wird hochgeladen...' : logoUrl ? 'Logo ersetzen' : 'Logo hochladen'}
            </SoftButton>
          </SoftBox>
        </Card>
      </SoftBox>

      {/* Favicon */}
      <SoftBox mb={3}>
        <Card>
          <SoftBox p={3}>
            <SoftTypography variant="h5" fontWeight="medium" gutterBottom>
              Favicon
            </SoftTypography>
            <SoftTypography variant="body2" color="text" mb={2}>
              Laden Sie ein Favicon hoch (PNG, ICO – max. 2 MB, empfohlen: 32x32 px).
            </SoftTypography>
            {faviconPreviewUrl && (
              <SoftBox mb={2}>
                <img
                  src={faviconPreviewUrl}
                  alt="Favicon Vorschau"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                  style={{ maxHeight: 32, maxWidth: 32, objectFit: 'contain', border: '1px solid #e2e8f0', borderRadius: 4 }}
                />
              </SoftBox>
            )}
            <input type="file" accept="image/*" ref={faviconInputRef} style={{ display: 'none' }} onChange={handleFaviconUpload} />
            <SoftButton
              variant="outlined" color="info"
              onClick={() => faviconInputRef.current && faviconInputRef.current.click()}
              disabled={isUploadingFavicon}
            >
              {isUploadingFavicon ? 'Wird hochgeladen...' : faviconUrl ? 'Favicon ersetzen' : 'Favicon hochladen'}
            </SoftButton>
          </SoftBox>
        </Card>
      </SoftBox>

      {/* Portal-Domain, Support & Home */}
      <SoftBox mb={3}>
        <Card>
          <SoftBox p={3}>
            <SoftTypography variant="h5" fontWeight="medium" gutterBottom>
              Portal-Domain, Support &amp; Home
            </SoftTypography>
            <SoftTypography variant="body2" color="text" mb={2}>
              Hinterlegen Sie hier eine eigene Portal-Domain und optionale Inhalte fuer das eingeschraenkte Kundenportal.
            </SoftTypography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SoftTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                  Portal-Domain
                </SoftTypography>
                <SoftInput
                  value={portalDomain}
                  onChange={(e) => setPortalDomain(e.target.value)}
                  placeholder="portal.schloermann.systems"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftBox mt={{ xs: 0, md: 2.75 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={restrictedPortalEnabled}
                        onChange={(e) => setRestrictedPortalEnabled(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <SoftTypography variant="caption" color="text">
                        Eingeschraenktes Portal aktivieren (IaaS + SBOffice)
                      </SoftTypography>
                    }
                  />
                </SoftBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                  Support-E-Mail
                </SoftTypography>
                <SoftInput
                  value={supportEmail}
                  onChange={(e) => setSupportEmail(e.target.value)}
                  placeholder="support@example.com"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                  Support-Telefon
                </SoftTypography>
                <SoftInput
                  value={supportPhone}
                  onChange={(e) => setSupportPhone(e.target.value)}
                  placeholder="+49 ..."
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SoftTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                  Home-Titel
                </SoftTypography>
                <SoftInput
                  value={portalHomeTitle}
                  onChange={(e) => setPortalHomeTitle(e.target.value)}
                  placeholder="Willkommen im Schloermann Portal"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <SoftTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                  Home-Einleitung
                </SoftTypography>
                <SoftInput
                  multiline
                  minRows={3}
                  value={portalHomeIntro}
                  onChange={(e) => setPortalHomeIntro(e.target.value)}
                  placeholder="Kurzer Infotext fuer die Startseite"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <SoftTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                  Home-Abschnitte (JSON)
                </SoftTypography>
                <SoftInput
                  multiline
                  minRows={6}
                  value={portalHomeSectionsText}
                  onChange={(e) => setPortalHomeSectionsText(e.target.value)}
                  placeholder='[{"title":"Ihre IaaS-Services","description":"...","icon":"cloud","route":"/iaas/server","action_label":"Oeffnen"}]'
                  fullWidth
                />
              </Grid>
            </Grid>
          </SoftBox>
        </Card>
      </SoftBox>

      {/* Produktauswahl, Namen & Preise */}
      <SoftBox mb={3}>
        <Card>
          <SoftBox p={3}>
            <SoftTypography variant="h5" fontWeight="medium" gutterBottom>
              Produkte, Namen &amp; Endkundenpreise
            </SoftTypography>
            <SoftTypography variant="body2" color="text" mb={2}>
              Aktivieren oder deaktivieren Sie Produkte und hinterlegen Sie optional einen eigenen Anzeigenamen und Endkundenpreis. Leere Felder verwenden die UDS-Standardwerte.
            </SoftTypography>
            <SoftBox mb={2}>
              <SoftButton variant="text" color="info" size="small" onClick={() => setSelectedSlugs(null)}>
                Alle auswaehlen / zuruecksetzen
              </SoftButton>
            </SoftBox>

            {Object.keys(productsByCategory).map(function(category) {
              var prods = productsByCategory[category];
              return (
                <SoftBox key={category} mb={3}>
                  <SoftTypography
                    variant="caption" fontWeight="bold" color="text" display="block" mb={1}
                    style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
                  >
                    {CATEGORY_LABELS[category] || category}
                  </SoftTypography>

                  {/* Spaltenheader */}
                  <Grid container spacing={1} mb={0.5} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                        Produkt (aktiv/inaktiv)
                      </SoftTypography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                        UDS-Standardname
                      </SoftTypography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                        Ihr Produktname (optional)
                      </SoftTypography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <SoftTypography variant="caption" color="secondary" fontWeight="medium">
                        Ihr Endkundenpreis (optional)
                      </SoftTypography>
                    </Grid>
                  </Grid>

                  {prods.map(function(p) {
                    var enabled = isSlugSelected(p.slug);
                    return (
                      <Grid container spacing={1} key={p.slug} alignItems="center" mb={0.75}>
                        {/* Checkbox */}
                        <Grid item xs={12} md={3}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={enabled}
                                onChange={() => handleProductToggle(p.slug)}
                                size="small"
                              />
                            }
                            label={
                              <SoftTypography variant="caption" fontWeight={enabled ? 'medium' : 'regular'} color={enabled ? 'dark' : 'secondary'}>
                                {enabled ? 'Aktiv' : 'Inaktiv'}
                              </SoftTypography>
                            }
                          />
                        </Grid>

                        {/* UDS-Standardname (read-only) */}
                        <Grid item xs={12} md={3}>
                          <SoftTypography variant="caption" color="text">
                            {p.name}
                            {p.display_price ? (
                              <SoftTypography component="span" variant="caption" color="secondary">
                                {' / ' + p.display_price}
                              </SoftTypography>
                            ) : null}
                          </SoftTypography>
                        </Grid>

                        {/* Eigener Name */}
                        <Grid item xs={12} md={3}>
                          <SoftInput
                            value={customNames[p.slug] || ''}
                            onChange={(e) => handleNameChange(p.slug, e.target.value)}
                            placeholder={p.name}
                            disabled={!enabled}
                            size="small"
                            fullWidth
                          />
                        </Grid>

                        {/* Eigener Preis */}
                        <Grid item xs={12} md={3}>
                          <SoftInput
                            value={customPrices[p.slug] || ''}
                            onChange={(e) => handlePriceChange(p.slug, e.target.value)}
                            placeholder={p.display_price || 'z.B. ab 19,99 EUR/Monat'}
                            disabled={!enabled}
                            size="small"
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    );
                  })}

                  <Divider style={{ marginTop: 8 }} />
                </SoftBox>
              );
            })}
          </SoftBox>
        </Card>
      </SoftBox>

      {/* Speichern */}
      <SoftBox display="flex" alignItems="center">
        <SoftBox mr={2}>
          <SoftButton variant="gradient" color="primary" onClick={handleSaveSettings} disabled={isSaving}>
            {isSaving ? 'Wird gespeichert...' : 'Einstellungen speichern'}
          </SoftButton>
        </SoftBox>
        {saveMessage !== '' && (
          <SoftTypography variant="body2" color={saveMessage.indexOf('Fehler') >= 0 ? 'error' : 'success'}>
            {saveMessage}
          </SoftTypography>
        )}
      </SoftBox>

    </SoftBox>
  );
}

export default PartnerBrandingSettings;
